import axios from 'axios';

// Warn if environment variables are missing
if (!process.env.REACT_APP_API_URL) {
  console.warn('[API] REACT_APP_API_URL is not defined, falling back to "/api"');
}
if (!process.env.REACT_APP_CLIENT_URL) {
  console.warn('[API] REACT_APP_CLIENT_URL is not defined, falling back to "/" for redirects');
}

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// 🔍 Request interceptor – log outgoing requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  
  // 📝 Log which request is being made
  // console.group(`[API] Request to ${config.url}`);
  // console.log('Token present:', !!token);
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // console.log('Token value:', token.substring(0, 20) + '...');
  }

  // Set Content-Type only if not FormData
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  // console.log('Headers:', config.headers);
  // console.groupEnd();

  return config;
});

// ⚠️ Response interceptor – handle errors like 401
API.interceptors.response.use(
  (response) => {
    // console.log(`[API] Response from ${response.config.url}`, response);
    return response;
  },
  (error) => {
    // console.error(`[API] Error from ${error.config?.url || 'unknown'}`, error);

    if (error.response?.status === 401) {
      // console.warn('[API] 401 Unauthorized – clearing token and redirecting to login');
      localStorage.removeItem('token');

      window.location.href = process.env.REACT_APP_CLIENT_URL
        ? `${process.env.REACT_APP_CLIENT_URL}/`
        : '/';
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }

    return Promise.reject(error);
  }
);

export default API;

// Generic API call wrapper
export const apiCall = async (url, options = {}) => {
  try {
    const response = await API({
      url,
      method: options.method || 'GET',
      data: options.body ? JSON.parse(options.body) : undefined,
      ...options
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// ===========================
// API Token Management
// ===========================

// API Users
export const getApiUsers = () => apiCall('/api-users');
export const createApiUser = (data) => apiCall('/api-users', {
  method: 'POST',
  body: JSON.stringify(data)
});
export const updateApiUser = (id, data) => apiCall(`/api-users/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data)
});
export const deleteApiUser = (id) => apiCall(`/api-users/${id}`, {
  method: 'DELETE'
});
export const getApiUsersStatistics = () => apiCall('/api-users/statistics');

// API Tokens
export const getTokens = () => apiCall('/tokens');
export const createToken = (data) => apiCall('/tokens', {
  method: 'POST',
  body: JSON.stringify(data)
});
export const updateToken = (id, data) => apiCall(`/tokens/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data)
});
export const deleteToken = (id) => apiCall(`/tokens/${id}`, {
  method: 'DELETE'
});
export const resetTokenRateLimit = (id) => apiCall(`/tokens/${id}/reset`, {
  method: 'POST'
});